import { useEffect, useMemo, useState } from "react";
import { MediaField } from "@/components/admin/MediaField";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listAdminProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  reorderProducts,
  upsertSchema,
  type AdminProduct,
  type ProductInput,
} from "@/lib/admin/products.functions";
import { formatPaise } from "@/lib/format/money";
import {
  INELIGIBLE_LABEL,
  healthProductIneligibleReasons,
  isLowStock,
} from "@/lib/health-products/eligibility";

export const Route = createFileRoute("/_admin/admin/products")({
  component: ProductsAdminPage,
});

type FormState = {
  name: string;
  note: string;
  image_url: string;
  alt_text: string;
  is_demo: boolean;
  is_active: boolean;
  code: string;
  slug: string;
  brand: string;
  product_type: string;
  category: string;
  description: string;
  gallery_urls: string[];
  price_rupees: string;
  mrp_rupees: string;
  gst_rate_percent: string;
  price_includes_gst: "" | "yes" | "no";
  pack_size: string;
  net_quantity: string;
  unit: string;
  track_stock: boolean;
  stock_quantity: string;
  low_stock_threshold: string;
  is_published: boolean;
  is_purchasable: boolean;
  seo_title: string;
  seo_description: string;
  ingredients: string;
  directions: string;
  warnings: string;
  batch_number: string;
  expiry_date: string;
  shipping_weight_grams: string;
  shipping_notes: string;
};

const s = (v: string | number | null | undefined) => (v == null ? "" : String(v));

function emptyForm(): FormState {
  return {
    name: "", note: "", image_url: "", alt_text: "", is_demo: false, is_active: true,
    code: "", slug: "", brand: "", product_type: "", category: "", description: "",
    gallery_urls: [], price_rupees: "", mrp_rupees: "", gst_rate_percent: "",
    price_includes_gst: "", pack_size: "", net_quantity: "", unit: "",
    track_stock: false, stock_quantity: "", low_stock_threshold: "",
    is_published: false, is_purchasable: false, seo_title: "", seo_description: "",
    ingredients: "", directions: "", warnings: "", batch_number: "", expiry_date: "",
    shipping_weight_grams: "", shipping_notes: "",
  };
}

function fromRow(p: AdminProduct): FormState {
  return {
    name: p.name,
    note: p.note,
    image_url: p.image_url ?? "",
    alt_text: p.alt_text ?? "",
    is_demo: p.is_demo,
    is_active: p.is_active,
    code: s(p.code),
    slug: s(p.slug),
    brand: s(p.brand),
    product_type: s(p.product_type),
    category: s(p.category),
    description: s(p.description),
    gallery_urls: p.gallery_urls ?? [],
    price_rupees: p.price_paise == null ? "" : String(p.price_paise / 100),
    mrp_rupees: p.mrp_paise == null ? "" : String(p.mrp_paise / 100),
    gst_rate_percent: s(p.gst_rate_percent),
    price_includes_gst: p.price_includes_gst == null ? "" : p.price_includes_gst ? "yes" : "no",
    pack_size: s(p.pack_size),
    net_quantity: s(p.net_quantity),
    unit: s(p.unit),
    track_stock: p.track_stock,
    stock_quantity: s(p.stock_quantity),
    low_stock_threshold: s(p.low_stock_threshold),
    is_published: p.is_published,
    is_purchasable: p.is_purchasable,
    seo_title: s(p.seo_title),
    seo_description: s(p.seo_description),
    ingredients: s(p.ingredients),
    directions: s(p.directions),
    warnings: s(p.warnings),
    batch_number: s(p.batch_number),
    expiry_date: s(p.expiry_date),
    shipping_weight_grams: s(p.shipping_weight_grams),
    shipping_notes: s(p.shipping_notes),
  };
}

/** Blank → null; non-numeric text → NaN so validation reports it. */
const num = (v: string) => (v.trim() === "" ? null : Number(v.replace(/[, ]/g, "")));
const rupeesToPaise = (v: string) => {
  const n = num(v);
  return n == null || Number.isNaN(n) ? n : Math.round(n * 100);
};

function toPayload(f: FormState) {
  return {
    name: f.name, note: f.note, image_url: f.image_url, alt_text: f.alt_text,
    is_demo: f.is_demo, is_active: f.is_active, code: f.code, slug: f.slug,
    brand: f.brand, product_type: f.product_type, category: f.category,
    description: f.description, gallery_urls: f.gallery_urls,
    price_paise: rupeesToPaise(f.price_rupees), mrp_paise: rupeesToPaise(f.mrp_rupees),
    gst_rate_percent: num(f.gst_rate_percent),
    price_includes_gst: f.price_includes_gst === "" ? null : f.price_includes_gst === "yes",
    pack_size: f.pack_size, net_quantity: num(f.net_quantity), unit: f.unit,
    track_stock: f.track_stock, stock_quantity: num(f.stock_quantity),
    low_stock_threshold: num(f.low_stock_threshold), is_published: f.is_published,
    is_purchasable: f.is_purchasable, seo_title: f.seo_title,
    seo_description: f.seo_description, ingredients: f.ingredients,
    directions: f.directions, warnings: f.warnings, batch_number: f.batch_number,
    expiry_date: f.expiry_date, shipping_weight_grams: num(f.shipping_weight_grams),
    shipping_notes: f.shipping_notes,
  };
}

const REORDER_ERROR_MSG: Record<string, string> = {
  incomplete_id_list: "Reorder must include every product. Reloading the list.",
  duplicate_ids: "Reorder contained duplicate rows.",
  unknown_id: "Reorder referenced an unknown product.",
  empty_id_list: "Nothing to reorder.",
  forbidden: "You don't have permission to reorder products.",
};

function ProductsAdminPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listAdminProducts);
  const createFn = useServerFn(createProduct);
  const updateFn = useServerFn(updateProduct);
  const deleteFn = useServerFn(deleteProduct);
  const reorderFn = useServerFn(reorderProducts);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "products"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });

  const [editing, setEditing] = useState<AdminProduct | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<AdminProduct | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) setForm(fromRow(editing));
    setErrors({});
  }, [editing]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] }),
      queryClient.invalidateQueries({ queryKey: ["products", "public"] }),
      queryClient.invalidateQueries({ queryKey: ["health-products"] }),
    ]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; patch: ProductInput }) => {
      if (payload.id === null) {
        return createFn({ data: payload.patch as never });
      }
      return updateFn({ data: { id: payload.id, patch: payload.patch } as never });
    },
    onSuccess: async () => {
      toast.success("Product saved");
      setEditing(null);
      setErrors({});
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Couldn't save the product: ${msg}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Product deleted");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      toast.error("Couldn't delete the product.");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderFn({ data: { ids } as never }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "products"] });
      const previous = queryClient.getQueryData<AdminProduct[]>(["admin", "products"]);
      if (previous) {
        const map = new Map(previous.map((p) => [p.id, p]));
        const reordered = ids
          .map((id, i) => {
            const row = map.get(id);
            return row ? { ...row, sort_order: (i + 1) * 10 } : null;
          })
          .filter((r): r is AdminProduct => r !== null);
        queryClient.setQueryData(["admin", "products"], reordered);
      }
      return { previous };
    },
    onError: async (err, _vars, ctx) => {
      console.error(err);
      if (ctx?.previous) queryClient.setQueryData(["admin", "products"], ctx.previous);
      const code = err instanceof Error ? err.message : "reorder_failed";
      toast.error(REORDER_ERROR_MSG[code] ?? "Couldn't reorder products.");
      await queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const products = useMemo(() => data ?? [], [data]);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= products.length) return;
    const ids = products.map((p) => p.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    reorderMutation.mutate(ids);
  };

  const handleSave = () => {
    const parsed = upsertSchema.safeParse(toPayload(form));
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    const id = editing && editing !== "new" ? editing.id : null;
    saveMutation.mutate({ id, patch: parsed.data });
  };

  if (isLoading) {
    return (
      <div>
        <AdminPageHeader title="Products" description="Manage the Health Products catalogue." />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <AdminPageHeader title="Products" />
        <Alert variant="destructive">
          <AlertDescription>
            Couldn't load products.{" "}
            <button className="underline" onClick={() => refetch()}>Retry</button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const e = errors;

  return (
    <div>
      <AdminPageHeader
        title="Products"
        description="Health Products catalogue. Published, priced products appear on the public Health Products page."
        actions={
          <ImportButtons
            moduleId="products"
            showExport
            exportName="health-products"
            exportRows={() => products}
            onCompleted={() => void invalidate()}
          />
        }
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {products.length} product{products.length === 1 ? "" : "s"}
        </p>
        <Button onClick={() => setEditing("new")} className="gap-2">
          <Plus className="h-4 w-4" aria-hidden /> New product
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No products yet. Create the first one.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 w-24">Order</th>
                <th className="p-3">Name</th>
                <th className="p-3">Price</th>
                <th className="p-3">Stock</th>
                <th className="p-3">Status</th>
                <th className="p-3">Website status</th>
                <th className="p-3 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => {
                const reasons = healthProductIneligibleReasons(p);
                return (
                  <tr key={p.id} className="border-t border-border align-top">
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0 || reorderMutation.isPending} aria-label="Move up">
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === products.length - 1 || reorderMutation.isPending} aria-label="Move down">
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-foreground">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.code ?? "No SKU"}
                        {p.category ? ` · ${p.category}` : ""}
                      </div>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      {p.price_paise == null ? <span className="text-muted-foreground">—</span> : formatPaise(p.price_paise)}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      {!p.track_stock ? (
                        <span className="text-muted-foreground">Not tracked</span>
                      ) : (
                        <span>
                          {p.stock_quantity ?? "—"}
                          {isLowStock(p) ? <Badge variant="outline" className="ml-2">Low</Badge> : null}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        <Badge variant={p.is_active ? "secondary" : "outline"}>{p.is_active ? "Active" : "Off"}</Badge>
                        <Badge variant={p.is_published ? "secondary" : "outline"}>{p.is_published ? "Published" : "Hidden"}</Badge>
                        {p.is_demo ? <Badge variant="outline">Demo</Badge> : null}
                      </div>
                    </td>
                    <td className="p-3 text-xs">
                      {reasons.length === 0 ? (
                        <span className="font-medium text-primary">On website</span>
                      ) : (
                        <span className="text-muted-foreground">
                          Not shown: {reasons.map((r) => INELIGIBLE_LABEL[r]).join(", ")}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(p)} aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(p)} aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New product" : "Edit product"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6">
            <Group title="Basics">
              <FieldRow label="Name *" error={e.name}>
                <Input value={form.name} onChange={(ev) => set("name", ev.target.value)} />
              </FieldRow>
              <Two>
                <FieldRow label="SKU / product code" hint="Required to publish. Used to match rows on import." error={e.code}>
                  <Input value={form.code} onChange={(ev) => set("code", ev.target.value)} />
                </FieldRow>
                <FieldRow label="Slug" hint="Web address, e.g. organic-honey. Required to publish." error={e.slug}>
                  <Input value={form.slug} onChange={(ev) => set("slug", ev.target.value.toLowerCase())} />
                </FieldRow>
              </Two>
              <Two>
                <FieldRow label="Category" error={e.category}>
                  <Input value={form.category} onChange={(ev) => set("category", ev.target.value)} />
                </FieldRow>
                <FieldRow label="Brand" error={e.brand}>
                  <Input value={form.brand} onChange={(ev) => set("brand", ev.target.value)} />
                </FieldRow>
              </Two>
              <FieldRow label="Product type" error={e.product_type}>
                <Input value={form.product_type} onChange={(ev) => set("product_type", ev.target.value)} />
              </FieldRow>
              <FieldRow label="Short note" hint="One-line summary shown on product cards." error={e.note}>
                <Textarea rows={2} value={form.note} onChange={(ev) => set("note", ev.target.value)} />
              </FieldRow>
              <FieldRow label="Full description" error={e.description}>
                <Textarea rows={4} value={form.description} onChange={(ev) => set("description", ev.target.value)} />
              </FieldRow>
            </Group>

            <Group title="Images">
              <FieldRow label="Primary image" hint="Required to publish." error={e.image_url}>
                <MediaField
                  value={form.image_url}
                  onChange={(next) => set("image_url", next)}
                  onAltSuggestion={(alt) => setForm((prev) => (prev.alt_text.trim() ? prev : { ...prev, alt_text: alt }))}
                />
              </FieldRow>
              <FieldRow label="Alt text" hint="Leave blank to use the product name." error={e.alt_text}>
                <Input value={form.alt_text} onChange={(ev) => set("alt_text", ev.target.value)} />
              </FieldRow>
              <FieldRow label="Additional images" error={e.gallery_urls}>
                <div className="grid gap-3">
                  {form.gallery_urls.map((url, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="flex-1">
                        <MediaField
                          value={url}
                          onChange={(next) =>
                            set("gallery_urls", form.gallery_urls.map((u, j) => (j === idx ? next : u)))
                          }
                        />
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Remove image"
                        onClick={() => set("gallery_urls", form.gallery_urls.filter((_, j) => j !== idx))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-fit gap-1"
                    onClick={() => set("gallery_urls", [...form.gallery_urls, ""])}
                    disabled={form.gallery_urls.length >= 12}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add image
                  </Button>
                </div>
              </FieldRow>
            </Group>

            <Group title="Price and tax (INR)">
              <Two>
                <FieldRow label="Selling price (₹)" hint="Required to publish." error={e.price_paise}>
                  <Input inputMode="decimal" value={form.price_rupees} onChange={(ev) => set("price_rupees", ev.target.value)} />
                </FieldRow>
                <FieldRow label="MRP (₹, optional)" error={e.mrp_paise}>
                  <Input inputMode="decimal" value={form.mrp_rupees} onChange={(ev) => set("mrp_rupees", ev.target.value)} />
                </FieldRow>
              </Two>
              <Two>
                <FieldRow label="GST rate (%)" hint="Required to publish." error={e.gst_rate_percent}>
                  <Input inputMode="decimal" value={form.gst_rate_percent} onChange={(ev) => set("gst_rate_percent", ev.target.value)} />
                </FieldRow>
                <FieldRow label="Price includes GST?" hint="Required to publish." error={e.price_includes_gst}>
                  <Select
                    value={form.price_includes_gst || "unset"}
                    onValueChange={(v) => set("price_includes_gst", v === "unset" ? "" : (v as "yes" | "no"))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Not set</SelectItem>
                      <SelectItem value="yes">Yes, GST included</SelectItem>
                      <SelectItem value="no">No, GST added on top</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
              </Two>
            </Group>

            <Group title="Pack">
              <div className="grid gap-4 sm:grid-cols-3">
                <FieldRow label="Pack size" hint="e.g. Jar of 30 capsules" error={e.pack_size}>
                  <Input value={form.pack_size} onChange={(ev) => set("pack_size", ev.target.value)} />
                </FieldRow>
                <FieldRow label="Net quantity" error={e.net_quantity}>
                  <Input inputMode="decimal" value={form.net_quantity} onChange={(ev) => set("net_quantity", ev.target.value)} />
                </FieldRow>
                <FieldRow label="Unit" hint="g, ml, capsules…" error={e.unit}>
                  <Input value={form.unit} onChange={(ev) => set("unit", ev.target.value)} />
                </FieldRow>
              </div>
            </Group>

            <Group title="Stock">
              <Toggle id="track_stock" checked={form.track_stock} onChange={(v) => set("track_stock", v)} label="Track stock" />
              {form.track_stock ? (
                <Two>
                  <FieldRow label="Stock quantity" error={e.stock_quantity}>
                    <Input inputMode="numeric" value={form.stock_quantity} onChange={(ev) => set("stock_quantity", ev.target.value)} />
                  </FieldRow>
                  <FieldRow label="Low-stock threshold" error={e.low_stock_threshold}>
                    <Input inputMode="numeric" value={form.low_stock_threshold} onChange={(ev) => set("low_stock_threshold", ev.target.value)} />
                  </FieldRow>
                </Two>
              ) : null}
            </Group>

            <Group title="Visibility">
              <Toggle id="is_active" checked={form.is_active} onChange={(v) => set("is_active", v)} label="Active" />
              <Toggle id="is_published" checked={form.is_published} onChange={(v) => set("is_published", v)} label="Published on the Health Products page" />
              <Toggle id="is_purchasable" checked={form.is_purchasable} onChange={(v) => set("is_purchasable", v)} label="Purchasable (online ordering opens in a later phase)" />
              <Toggle id="is_demo" checked={form.is_demo} onChange={(v) => set("is_demo", v)} label="Demo content (never shown to customers)" />
            </Group>

            <Group title="Product details">
              <FieldRow label="Ingredients" error={e.ingredients}>
                <Textarea rows={3} value={form.ingredients} onChange={(ev) => set("ingredients", ev.target.value)} />
              </FieldRow>
              <FieldRow label="Directions for use" error={e.directions}>
                <Textarea rows={3} value={form.directions} onChange={(ev) => set("directions", ev.target.value)} />
              </FieldRow>
              <FieldRow label="Warnings" error={e.warnings}>
                <Textarea rows={3} value={form.warnings} onChange={(ev) => set("warnings", ev.target.value)} />
              </FieldRow>
              <Two>
                <FieldRow label="Batch number" error={e.batch_number}>
                  <Input value={form.batch_number} onChange={(ev) => set("batch_number", ev.target.value)} />
                </FieldRow>
                <FieldRow label="Expiry date" error={e.expiry_date}>
                  <Input type="date" value={form.expiry_date} onChange={(ev) => set("expiry_date", ev.target.value)} />
                </FieldRow>
              </Two>
            </Group>

            <Group title="Shipping">
              <FieldRow label="Shipping weight (grams)" error={e.shipping_weight_grams}>
                <Input inputMode="numeric" value={form.shipping_weight_grams} onChange={(ev) => set("shipping_weight_grams", ev.target.value)} />
              </FieldRow>
              <FieldRow label="Shipping / fulfilment notes" error={e.shipping_notes}>
                <Textarea rows={2} value={form.shipping_notes} onChange={(ev) => set("shipping_notes", ev.target.value)} />
              </FieldRow>
            </Group>

            <Group title="Search engines">
              <FieldRow label="SEO title" error={e.seo_title}>
                <Input value={form.seo_title} onChange={(ev) => set("seo_title", ev.target.value)} />
              </FieldRow>
              <FieldRow label="Meta description" error={e.seo_description}>
                <Textarea rows={2} value={form.seo_description} onChange={(ev) => set("seo_description", ev.target.value)} />
              </FieldRow>
            </Group>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes “{confirmDelete?.name}” permanently. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-4">
      <h3 className="border-b border-border pb-1 text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

function Two({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function Toggle({ id, checked, onChange, label }: { id: string; checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );
}

function FieldRow({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      {children}
      {hint && !error ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
